import useScanCamera from './useScanCamera.js';

/**
 * Camera component for scanning labels via getUserMedia + OCR.
 * Supports burst mode (camera stays open between scans), anti-doublon, and auto-manual fallback.
 *
 * @param {{ onScanResult: (result: object) => void, offline: boolean, scannedSerials?: string[] }} props
 */
export default function ScanCamera({ onScanResult, offline = false, scannedSerials = [] }) {
  const {
    videoRef, canvasRef, streaming, captured, scanning, ocrResult,
    manualSerial, error, forceManual, duplicateWarning,
    setManualSerial, startCamera, stopCamera, capture, sendForOCR,
    confirmResult, retake,
  } = useScanCamera({ onScanResult, offline, scannedSerials });

  const showManualInput = offline || forceManual || (error && captured && !ocrResult);

  return (
    <div className="space-y-3">
      {/* Live camera view (stays open in burst mode) */}
      <div className="relative">
        <video ref={videoRef} className="w-full rounded-lg bg-black" playsInline muted />
        {streaming && !captured && (
          <>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <p className="text-white text-sm font-medium bg-black/50 rounded-lg px-3 py-1 mb-3">Cadrez l'etiquette FNUCI</p>
              <div className="border-2 border-yellow-400 rounded w-3/4 h-1/2 opacity-70" />
            </div>
            <button
              onClick={capture}
              className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-white rounded-full w-16 h-16 border-4 border-blue-600 shadow-lg flex items-center justify-center"
              aria-label="Capturer"
            >
              <div className="w-12 h-12 bg-blue-600 rounded-full" />
            </button>
          </>
        )}
        {!streaming && (
          <button onClick={startCamera} className="w-full bg-blue-600 text-white rounded-xl py-3 font-medium">
            Ouvrir la caméra
          </button>
        )}
      </div>

      <canvas ref={canvasRef} className="hidden" />

      {/* Captured preview + OCR action */}
      {captured && !ocrResult && !showManualInput && (
        <div className="space-y-3">
          <img src={`data:image/jpeg;base64,${captured}`} alt="Capture" className="w-full rounded-lg" />
          <button
            onClick={sendForOCR}
            disabled={scanning}
            className="w-full bg-blue-600 text-white rounded-xl py-3 font-medium disabled:opacity-50"
          >
            {scanning ? 'Analyse en cours...' : 'Analyser (OCR)'}
          </button>
          <button onClick={retake} className="w-full border border-gray-300 rounded-xl py-2 text-sm">
            Reprendre la photo
          </button>
        </div>
      )}

      {/* Duplicate warning */}
      {duplicateWarning && (
        <div className="bg-red-100 border border-red-400 rounded-xl p-3 text-center">
          <p className="text-red-800 font-bold">{duplicateWarning}</p>
          <button onClick={retake} className="mt-2 text-sm text-red-600 underline">
            Scanner un autre
          </button>
        </div>
      )}

      {/* OCR Result */}
      {ocrResult && !duplicateWarning && (
        <OcrResultView
          ocrResult={ocrResult}
          onConfirm={confirmResult}
          onRetake={retake}
        />
      )}

      {/* Manual input with photo stored for later OCR (offline, OCR fail, or forced after 3 failures) */}
      {showManualInput && captured && !ocrResult && (
        <ManualInput
          value={manualSerial}
          onChange={setManualSerial}
          onSubmit={(serial) => confirmResult(serial, '', 'manual_photo')}
          forceManual={forceManual}
          hasPhoto={!!captured}
          photoBase64={captured}
        />
      )}

      {error && <p className="text-red-600 text-sm text-center">{error}</p>}
    </div>
  );
}

/** @param {{ ocrResult: object, onConfirm: Function, onRetake: Function }} props */
function OcrResultView({ ocrResult, onConfirm, onRetake }) {
  const ds3 = ocrResult.ds3;
  return (
    <div className="bg-green-50 border border-green-200 rounded-xl p-4 space-y-2">
      <p className="font-medium text-green-800">Résultat OCR</p>
      <p className="text-sm">FNUCI : <strong>{ocrResult.serialNumber || '---'}</strong></p>
      <p className="text-sm">Boite : <strong>{ocrResult.secondaryId || '---'}</strong></p>
      <p className="text-xs text-gray-500">
        Confiance : {Math.round((ocrResult.confidence || 0) * 100)}% ({ocrResult.method})
      </p>
      {ds3 && ds3.match && (
        <p className="text-xs text-emerald-700 bg-emerald-50 rounded px-2 py-1">
          DS3 : correspondance validee
        </p>
      )}
      {ds3 && ds3.warning && (
        <p className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-1">
          DS3 : {ds3.warning}
        </p>
      )}
      {ds3 && !ds3.valid && (
        <p className="text-xs text-red-700 bg-red-50 rounded px-2 py-1 font-medium">
          DS3 : conflit detecte — verifier manuellement
        </p>
      )}
      <div className="flex gap-2">
        <button
          onClick={() => onConfirm(ocrResult.serialNumber, ocrResult.secondaryId, ocrResult.method)}
          className="flex-1 bg-green-600 text-white rounded-xl py-2 font-medium"
        >
          Confirmer
        </button>
        <button
          onClick={onRetake}
          className="flex-1 border border-gray-300 rounded-xl py-2 text-sm"
        >
          Re-scanner
        </button>
      </div>
    </div>
  );
}

/** @param {{ value: string, onChange: Function, onSubmit: Function, forceManual: boolean, hasPhoto?: boolean, photoBase64?: string }} props */
function ManualInput({ value, onChange, onSubmit, forceManual, hasPhoto, photoBase64 }) {
  const handleSubmit = async (serial) => {
    // Store photo for later OCR verification when back online
    if (hasPhoto && photoBase64) {
      try {
        const { addToQueue } = await import('../offline/syncQueue.js');
        await addToQueue({
          type: 'ocr_verify',
          url: '/ocr_verify',
          method: 'POST',
          body: {
            serialNumber: serial,
            imageBase64: photoBase64,
            capturedAt: new Date().toISOString(),
          },
        });
      } catch (_err) { /* non-critical */ }
    }
    onSubmit(serial);
  };

  return (
    <div className="space-y-3">
      {/* Info banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-center">
        <p className="text-amber-800 text-sm font-medium">
          {forceManual ? 'OCR indisponible — saisie manuelle' : 'Mode hors ligne'}
        </p>
        {hasPhoto && (
          <p className="text-amber-600 text-xs mt-1">
            📸 Photo enregistrée — sera vérifiée par OCR au retour en ligne
          </p>
        )}
      </div>

      {/* Photo preview thumbnail */}
      {hasPhoto && photoBase64 && (
        <img src={`data:image/jpeg;base64,${photoBase64}`} alt="Capture" className="w-full h-24 object-cover rounded-lg opacity-70" />
      )}

      {/* Manual serial input */}
      <div className="flex gap-2">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          placeholder="AV2024A001"
          className="flex-1 border border-gray-200 rounded-xl px-3 py-3 text-lg font-mono"
        />
      </div>
      <button
        onClick={() => handleSubmit(value)}
        disabled={!value.trim()}
        className="w-full bg-orange-500 text-white rounded-xl py-3 font-medium disabled:opacity-50"
      >
        {hasPhoto ? '✓ Valider (photo + numéro)' : 'Valider manuellement'}
      </button>
    </div>
  );
}
